import React from "react";
import { AvatarRing } from "../../../common/comms/AvatarRing";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import { PRIORITY_LABELS } from "./supportEnums";
import { DEPARTMENT_TEXT, PRIORITY_TEXT, rowTreatment, slaCell } from "./ticketRowMeta";

/**
 * One row of the dense ticket list.
 *
 * The columns are fixed-width so the eye can scan straight down a single
 * dimension — SLA especially — which is the whole point of a queue this dense.
 * At narrow widths the least load-bearing columns (type, priority, requester)
 * drop out rather than compressing everything into unreadability.
 *
 * @param {Object} props
 * @param {Object} props.ticket - Normalized ticket.
 * @param {boolean} props.selected - Is this the open ticket.
 * @param {boolean} props.checked - Is this row ticked for a bulk action.
 * @param {(roomId: string) => void} props.onToggle
 * @param {(ticket: Object) => void} props.onSelect
 * @param {boolean} [props.dense=true] - False drops the type/priority columns.
 */
export function TicketRow({ ticket, selected, checked, onToggle, onSelect, dense = true }) {
  // The requester is a client; the owner is an employee — different caches.
  const { getClientName, getCoachName } = useCommsIdentity();
  const treatment = rowTreatment(ticket);
  const sla = slaCell(ticket);
  const clientName = ticket.client || getClientName(ticket.clientId) || "—";

  return (
    <div
      className={`flex items-center gap-1.5 border-b border-gray-100 border-l-[3px] px-2.5 py-1.5 text-xs transition-colors ${
        treatment.bar
      } ${selected ? "bg-info-bg" : `${treatment.row} hover:bg-gray-50`} ${
        treatment.muted ? "opacity-70" : ""
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(ticket.roomId)}
        aria-label={`Select ticket ${ticket.displayId}`}
        className="h-3.5 w-3.5 flex-shrink-0 accent-orange-primary"
      />

      <button
        type="button"
        onClick={() => onSelect(ticket)}
        aria-current={selected ? "true" : undefined}
        className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
      >
        <span
          className={`w-11 flex-shrink-0 font-mono text-[11px] font-bold ${
            ticket.breached && !treatment.struck ? "text-danger-dark" : "text-gray-500"
          }`}
        >
          {ticket.displayId}
        </span>

        <span className="min-w-0 flex-1 truncate">
          <span
            className={
              treatment.struck
                ? "text-gray-500 line-through"
                : "font-semibold text-gray-900"
            }
          >
            {ticket.subject}
          </span>
          {ticket.mergedIntoRoomId && (
            <span className="text-gray-500">
              {" "}
              · merged into{" "}
              <span className="font-mono font-bold">{ticket.mergedIntoRoomId}</span>
            </span>
          )}
          {!dense && (
            <span className="text-gray-500"> · {clientName}</span>
          )}
        </span>

        {dense && (
          <span className="hidden w-20 flex-shrink-0 truncate text-[11px] text-gray-500 xl:block">
            {clientName}
          </span>
        )}

        <span
          className={`hidden w-[70px] flex-shrink-0 text-[10px] font-bold uppercase lg:block ${
            treatment.muted
              ? "text-gray-400"
              : DEPARTMENT_TEXT[ticket.department] || "text-gray-500"
          }`}
        >
          {ticket.department || ticket.category || ""}
        </span>

        {dense && (
          <span className="hidden w-[72px] flex-shrink-0 truncate text-[11px] text-gray-700 2xl:block">
            {ticket.type || ""}
          </span>
        )}

        {dense && (
          <span
            className={`hidden w-[50px] flex-shrink-0 text-[10px] font-bold uppercase xl:block ${
              PRIORITY_TEXT[ticket.priority] || "text-gray-500"
            }`}
          >
            {treatment.struck ? "—" : PRIORITY_LABELS[ticket.priority] || ticket.priority}
          </span>
        )}

        <span
          className={`w-[86px] flex-shrink-0 rounded-sm px-1.5 py-px text-center font-mono text-[11px] font-bold ${sla.className}`}
        >
          {sla.label}
        </span>

        <span className="flex w-10 flex-shrink-0 justify-end">
          {ticket.assigneeId ? (
            <AvatarRing
              name={ticket.assigneeName || getCoachName(ticket.assigneeId) || "Agent"}
              colorIndex={String(ticket.assigneeId).charCodeAt(0) % 8}
              size="xs"
            />
          ) : (
            !treatment.struck && (
              <span
                aria-label="Unassigned"
                title="Unassigned"
                className="h-[19px] w-[19px] rounded-full border-[1.5px] border-dashed border-gray-300"
              />
            )
          )}
        </span>
      </button>
    </div>
  );
}

export default TicketRow;
